/**
 * UTM helpers.
 *
 * Links shared through messaging apps, native share sheets, email or PDFs never
 * send a referrer header, so GA4 buckets that traffic as "(direct) / (none)".
 * Tagging every outbound/shared link with UTM parameters makes it attributable.
 */

export interface UtmParams {
  source: string;
  medium: string;
  campaign?: string;
  term?: string;
  content?: string;
}

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];

/** Append UTM parameters to a URL without clobbering existing ones. */
export const withUtm = (url: string, { source, medium, campaign, term, content }: UtmParams): string => {
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://brrgr.com');
    const set = (key: UtmKey, value?: string) => {
      if (value && !u.searchParams.has(key)) u.searchParams.set(key, value);
    };
    set('utm_source', source);
    set('utm_medium', medium);
    set('utm_campaign', campaign);
    set('utm_term', term);
    set('utm_content', content);
    return u.toString();
  } catch {
    return url;
  }
};

/** Google Play requires campaign data inside a single `referrer` parameter. */
export const playStoreUrlWithUtm = (url: string, params: UtmParams): string => {
  try {
    const u = new URL(url);
    if (!u.searchParams.has('referrer')) {
      const referrer = new URLSearchParams({
        utm_source: params.source,
        utm_medium: params.medium,
        ...(params.campaign ? { utm_campaign: params.campaign } : {}),
      }).toString();
      u.searchParams.set('referrer', referrer);
    }
    return u.toString();
  } catch {
    return url;
  }
};

/** Apple uses `ct` (campaign token) / `pt` (provider token) instead of UTMs. */
export const appStoreUrlWithUtm = (url: string, campaignToken: string): string => {
  try {
    const u = new URL(url);
    if (!u.searchParams.has('ct')) u.searchParams.set('ct', campaignToken);
    if (!u.searchParams.has('mt')) u.searchParams.set('mt', '8');
    return u.toString();
  } catch {
    return url;
  }
};

const UTM_STORAGE_KEY = 'brrgr_utm';

/** Read UTM parameters from the current URL. */
export const readUtmFromLocation = (): Partial<Record<UtmKey, string>> => {
  if (typeof window === 'undefined') return {};
  const p = new URLSearchParams(window.location.search);
  const out: Partial<Record<UtmKey, string>> = {};
  UTM_KEYS.forEach((k) => {
    const v = p.get(k);
    if (v) out[k] = v;
  });
  return out;
};

/**
 * Persist the landing UTMs for the session so attribution survives the OAuth
 * round-trip (Google / Apple / Supabase) that would otherwise reset the source.
 */
export const persistLandingUtm = () => {
  if (typeof window === 'undefined') return;
  const current = readUtmFromLocation();
  if (Object.keys(current).length === 0) return;
  try {
    window.sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(current));
  } catch {}
};

export const getPersistedUtm = (): Partial<Record<UtmKey, string>> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(UTM_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<Record<UtmKey, string>>) : {};
  } catch {
    return {};
  }
};
